import { useNavigate } from 'react-router-dom'
import * as S from './OpsStyle'
import { formatNaira, useOpsOrders, useOpsTickets, useOpsVendors } from './opsData'

/**
 * What an operator needs on opening the console: what is waiting for them, and
 * whether anything is stuck.
 *
 * Every number counts real rows only — prototype and dummy records are filtered
 * out upstream in opsData. A console that quietly reports fixture data is worse
 * than one that reports nothing.
 */
export const OpsOverviewPage = ({ canManagePromotions = true }: { canManagePromotions?: boolean }) => {
  const navigate = useNavigate()
  const vendors = useOpsVendors()
  const orders = useOpsOrders()
  const tickets = useOpsTickets()

  const pendingVendors = vendors.filter((v) => v.verification === 'pending').length
  const openTickets = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed').length
  const liveOrders = orders.filter((o) =>
    ['paid', 'accepted', 'preparing', 'ready', 'delivery_requested', 'in_delivery'].includes(o.status),
  )
  const unpaid = orders.filter((o) => o.status === 'pending_payment')
  // An order the kitchen has not picked up. Chef-side used to have no button
  // for these at all, so they are worth surfacing on their own.
  const unaccepted = orders.filter((o) => o.status === 'paid')
  const completedValue = orders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + o.totalMinor, 0)

  return (
    <>
      <div>
        <S.H1>Operations overview</S.H1>
      </div>

      <S.StatRow>
        <S.StatButton type="button" onClick={() => navigate('/ops/verification')}>
          <S.StatValue>{pendingVendors}</S.StatValue>
          <S.StatLabel>Kitchens awaiting verification</S.StatLabel>
        </S.StatButton>
        <S.StatButton type="button" onClick={() => navigate('/ops/orders?status=paid')}>
          <S.StatValue>{unaccepted.length}</S.StatValue>
          <S.StatLabel>Paid, not yet accepted</S.StatLabel>
        </S.StatButton>
        <S.StatButton type="button" onClick={() => navigate('/ops/orders?status=in_flight')}>
          <S.StatValue>{liveOrders.length}</S.StatValue>
          <S.StatLabel>Orders in flight</S.StatLabel>
        </S.StatButton>
        <S.StatButton type="button" onClick={() => navigate('/ops/support')}>
          <S.StatValue>{openTickets}</S.StatValue>
          <S.StatLabel>Open support tickets</S.StatLabel>
        </S.StatButton>
        <S.StatButton type="button" onClick={() => navigate('/ops/orders?status=pending_payment')}>
          <S.StatValue>{unpaid.length}</S.StatValue>
          <S.StatLabel>Awaiting payment</S.StatLabel>
        </S.StatButton>
        <S.StatButton type="button" onClick={() => navigate('/ops/orders?status=completed')}>
          <S.StatValue>{formatNaira(completedValue)}</S.StatValue>
          <S.StatLabel>Completed order value</S.StatLabel>
        </S.StatButton>
      </S.StatRow>

      {canManagePromotions && <S.Card>
        <S.CardTitle>Discounts & promotions</S.CardTitle>
        <S.Note>Manage kitchen and dish campaigns, order service fees, and meal-plan rates.</S.Note>
        <S.Action onClick={() => navigate('/ops/promotions')}>Open promotions →</S.Action>
      </S.Card>}
      <S.Card>
        <S.CardTitle>Kitchens</S.CardTitle>
        <S.StatRow>
          <S.StatButton type="button" onClick={() => navigate('/ops/kitchens?filter=verified')}>
            <S.StatValue>{vendors.filter((v) => v.verification === 'verified').length}</S.StatValue>
            <S.StatLabel>Verified</S.StatLabel>
          </S.StatButton>
          <S.StatButton type="button" onClick={() => navigate('/ops/kitchens?filter=open')}>
            <S.StatValue>{vendors.filter((v) => v.isOpen).length}</S.StatValue>
            <S.StatLabel>Marked open by chef</S.StatLabel>
          </S.StatButton>
          <S.StatButton type="button" onClick={() => navigate('/ops/kitchens?filter=missing_cuisine')}>
            <S.StatValue>{vendors.filter((v) => !v.cuisine).length}</S.StatValue>
            <S.StatLabel>Missing cuisine</S.StatLabel>
          </S.StatButton>
          <S.StatButton type="button" onClick={() => navigate('/ops/kitchens?filter=missing_area')}>
            <S.StatValue>{vendors.filter((v) => !v.area).length}</S.StatValue>
            <S.StatLabel>Missing area</S.StatLabel>
          </S.StatButton>
        </S.StatRow>
        <S.Note>
          Chefs can update cuisine and primary kitchen locations from their profile. Missing areas reduce the relevance of discovery results.
        </S.Note>
      </S.Card>
    </>
  )
}
